import { Injectable, DOCUMENT, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ShepherdService } from 'angular-shepherd';
import { filter, map, of, Subject, switchMap } from 'rxjs';
import { take } from 'rxjs/operators';
import { StepOptions, Tour, StepOptionsButton, Step, StepOptionsAttachTo } from 'shepherd.js';

import { AlooraHeaderService } from '@core/aloora-title/aloora-header.service';
import { LOCAL_STORAGE_TOKEN } from '@core/domTokens';
import { UserProfileSettingsService } from '@core/user-profile-settings-service/user-profile-settings.service';
// eslint-disable-next-line no-restricted-imports
import { OVERVIEW_TABS } from '@widgets/cards/model/cards.model';

export type TutorialId =
  | 'ResultsTutorial'
  | 'OrderSamplesTutorial'
  | 'OrderDetailsTutorial'
  | 'OrderConfirmationTutorial'
  | 'OrderAnalysisTutorial'
  | 'YourLabsTutorial'
  | `YourLabs${Capitalize<OVERVIEW_TABS>}TabTutorial`
  | 'AuthorisationsTutorial';

type TutorialTour = {
  steps: TutorialStep[];
  id: TutorialId;
};

export type TutorialStep = StepOptions & { nextAction?: () => void; backAction?: () => void };

@Injectable()
export class TutorialService {
  private readonly shepherdService = inject(ShepherdService);
  private readonly localStorage = inject<Storage>(LOCAL_STORAGE_TOKEN);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly translate = inject(TranslateService);
  private readonly userProfileSettingsService = inject(UserProfileSettingsService);
  private readonly headerService = inject(AlooraHeaderService);

  public get currentTour(): Tour | null {
    return this.shepherdService.tourObject;
  }

  private readonly backButton: StepOptionsButton = {
    classes: 'tutorial-leading-button',
    text: '<mat-icon class="material-symbols-outlined">arrow_back</mat-icon>',
    action: () => this.shepherdService.back(),
  };

  private readonly nextButton: StepOptionsButton = {
    classes: 'tutorial-trailing-button',
    text: '<mat-icon class="material-symbols-outlined">arrow_forward</mat-icon>',
    action: () => this.shepherdService.next(),
  };

  private readonly startTutorialSubject$ = new Subject<TutorialTour>();
  constructor() {
    const dialog = inject(MatDialog);

    this.startTutorialSubject$
      .pipe(
        switchMap((tour) => {
          if (this.headerService.initialTutorial) {
            return this.userProfileSettingsService.showInitialTutorials$.pipe(
              take(1),
              filter((showInitialTutorials) => showInitialTutorials),
              map(() => tour),
            );
          }
          this.headerService.initialTutorial = false;
          return of(tour);
        }),
        switchMap((tour) => {
          if (dialog.openDialogs.length > 0) {
            return dialog.afterAllClosed.pipe(
              take(1),
              map(() => tour),
            );
          }
          return of(tour);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((tutorialTour) => {
        this.startTutorial(tutorialTour.id, tutorialTour.steps);
      });
  }

  public shouldShowInitialTutorial(tutorialId: TutorialId): boolean {
    return this.localStorage.getItem(this.getLocalStorageKey(tutorialId)) !== 'true';
  }

  public start(tutorialId: TutorialId, steps: TutorialStep[]) {
    this.startTutorialSubject$.next({ id: tutorialId, steps });
  }

  public complete() {
    if (this.shepherdService.isActive) {
      this.shepherdService.complete();
    }
  }

  public isCurrentStepIdActive(stepId: string): boolean {
    return this.shepherdService.isActive && this.getCurrentStep()?.id === stepId;
  }

  public nextStep() {
    if (this.shepherdService.isActive) {
      this.shepherdService.next();
    }
  }

  public showStepWithId(stepId: string | number) {
    if (this.shepherdService.isActive) {
      this.shepherdService.tourObject?.show(stepId);
    }
  }

  public clickOnTargetElement() {
    if (!this.shepherdService.isActive) {
      return;
    }
    const element = this.document.querySelector<HTMLButtonElement>('.shepherd-target');
    element?.click();
  }

  public cancel() {
    if (this.shepherdService.isActive) {
      this.shepherdService.cancel();
    }
  }

  private startTutorial(tutorialId: TutorialId, steps: TutorialStep[]): void {
    if (this.shepherdService.isActive) {
      this.shepherdService.cancel();
    }

    this.localStorage.setItem(this.getLocalStorageKey(tutorialId), 'true');

    this.shepherdService.defaultStepOptions = {
      cancelIcon: {
        enabled: true,
      },
      buttons: [this.backButton, this.nextButton],
      modalOverlayOpeningPadding: 5,
      modalOverlayOpeningRadius: 5,
      scrollTo: { behavior: 'smooth', block: 'center' },
      canClickTarget: false,
      when: {
        show: this.defaultShowFunction,
      },
    };

    this.shepherdService.modal = true;
    this.shepherdService.confirmCancel = false;

    // Adjust the show function of the steps to include the default show function
    const adjustedSteps: StepOptions[] = steps.map((step, stepIndex) => {
      const currentShowFunction = step.when?.show;
      const currentShowOnFunction = step.showOn;

      const adjustedNextButton: StepOptionsButton = { ...this.nextButton };
      if (step.nextAction) {
        adjustedNextButton.action = () => {
          step.nextAction?.();
        };
      }

      const adjustedBackButton: StepOptionsButton = { ...this.backButton };
      if (step.backAction) {
        adjustedBackButton.action = () => {
          step.backAction?.();
        };
      }

      return {
        ...step,
        title: this.translateTitle(step.title),
        text: this.translateStepText(step.text),
        when: {
          ...step.when,
          show: () => {
            currentShowFunction?.call(step as Step);
            this.defaultShowFunction();
          },
        },
        showOn: () => {
          const resultCurrentShowOnFunction = currentShowOnFunction?.() ?? true;
          if (currentShowFunction || step.beforeShowPromise) {
            return resultCurrentShowOnFunction;
          }
          return resultCurrentShowOnFunction && this.attachedElementInDOM(step.attachTo?.element);
        },
        buttons: [
          { ...this.backButton, disabled: stepIndex === 0 },
          ...(step.buttons ?? []).map((button) => ({
            ...button,
            text: this.translateTitle(button.text),
          })),
          { ...adjustedNextButton, disabled: stepIndex === steps.length - 1 },
        ],
      };
    });

    this.shepherdService.addSteps(adjustedSteps);
    this.shepherdService.start();
  }

  /**
   * Default function used to determine whether a step should be shown or not.
   * @param step - The step options.
   * @returns true if the element the step is attached to is inside of the dom otherwise false.
   */
  private attachedElementInDOM(attachedElement: StepOptionsAttachTo['element']): boolean {
    if (!attachedElement) {
      return true;
    }
    const element = typeof attachedElement === 'function' ? attachedElement() : attachedElement;
    let domElement: HTMLElement | null | undefined;
    if (typeof element === 'string') {
      domElement = this.document.querySelector<HTMLElement>(element);
    } else {
      domElement = element;
    }

    return !!domElement;
  }

  private readonly getCurrentStep = () => this.shepherdService.tourObject?.getCurrentStep();

  private readonly goToStep = (index: string) => this.shepherdService.tourObject?.show(index);

  private readonly getAllSteps = () => this.shepherdService.tourObject?.steps;

  private readonly defaultShowFunction = () => {
    const currentStep = this.getCurrentStep();
    const allActiveSteps =
      this.getAllSteps()?.filter((step: Step) =>
        step.options.showOn ? step.options.showOn() : true,
      ) ?? [];
    if (!currentStep) {
      return;
    }
    const currentStepElement = currentStep.getElement();
    const currentStepIndex = allActiveSteps.indexOf(currentStep);
    const totalSteps = allActiveSteps.length;
    const progress = this.document.createElement('div');
    progress.className = 'shepherd-progress-bar';
    for (let i = 0; i < totalSteps; i++) {
      const circle = this.document.createElement('div');
      circle.className = 'shepherd-progress-circle';
      if (i === currentStepIndex) {
        circle.classList.add('current');
      }
      circle.addEventListener('click', () => {
        const selectedStep = allActiveSteps[i];
        this.goToStep(selectedStep.id);
      });
      progress.appendChild(circle);
    }
    const header = currentStepElement?.querySelector('.shepherd-footer');
    header?.insertBefore(
      progress,
      currentStepElement?.querySelector('.tutorial-trailing-button') ?? null,
    );
  };

  private getLocalStorageKey(tutorialId: TutorialId): `hide${TutorialId}` {
    return `hide${tutorialId}`;
  }

  private translateStepText(text?: StepOptions['text']): StepOptions['text'] {
    if (!text) {
      return undefined;
    }
    if (Array.isArray(text)) {
      return text.map((t) => this.translate.instant(t));
    }
    if (typeof text === 'string') {
      return this.translate.instant(text);
    }

    return text;
  }

  private translateTitle(title?: string | (() => string)): string | (() => string) | undefined {
    if (!title) {
      return undefined;
    }
    if (typeof title === 'string') {
      return this.translate.instant(title);
    }
    return title;
  }
}
